import React, { useState, useEffect } from "react";
import useStyles from "./style";
import {
  Dialog,
  Typography,
  Button,
  IconButton,
  Grid,
  MenuItem,
  TextField,
  InputAdornment,
  Box,
} from "@mui/material";
import {
  CloseOutlined,
  EditOutlined,
  PersonAddAltOutlined,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import _ from "lodash";
import { useFormik } from "formik";
import { useDropzone } from "react-dropzone";
import * as yup from "yup";

//Libs
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useDispatch,useSelector } from "react-redux";

//Services
import AccountmanagerApi from "Services/Accountmanager";

//Data
import CountryCodeDropdown from "SharedComponents/CountryCodeDropdown";
import { getCountryCodes } from "Utils/country";

const ManagerCreate = (props) => {
  const { openDialog, handleClose } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const demoaccount_id = useSelector(
    (state) => state?.AuthReducer?.demoaccount_id || ""
  );
  const [passengerimage, setpassengerimage] = useState("/assets/images/user/avatar.png");
  const [countryCodeData, setCountryCodeData] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const maxFileSize = 5242880;

  const fileLengthValidator = (file) => {
    if (file.size > maxFileSize) {
      alert("File size is larger than 5MB");
      return { code: "file-too-large", message: "File too large" };
    }
    return null;
  };

  const { getRootProps, getInputProps, open } = useDropzone({
    noClick: true,
    noKeyboard: true,
    maxFiles: 1,
    accept: "image/*",
    validator: fileLengthValidator,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles && acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        const reader = new FileReader();
        reader.onload = () => {
          const base64String = reader.result;
          formik.setFieldValue("passengerProfileImage", file);
          setpassengerimage(base64String);
        };
        reader.readAsDataURL(file);
      }
    },
  });

  useEffect(() => {
    try {
      const countries = getCountryCodes();
      setCountryCodeData(countries);
    } catch (error) {
      console.error("Error fetching country codes:", error);
    }
  }, []);

  const handleAddManager = (formValues) => {
    dispatch(showLoader("Loading please wait..."));
    const data = {
      firstName: formValues?.firstName,
      lastName: formValues?.lastName,
      country: formValues?.country,
      countryCode: formValues?.countryCode,
      mobileNumber: formValues?.mobile,
      email: formValues?.email,
      image: formValues?.passengerProfileImage,
      password: formValues?.password,
      passConfirmation: formValues?.passConfirmation,
      gender: formValues?.gender,
      demoaccount_id: demoaccount_id || "",
    };

    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        dispatch(
          showSnackbar({
            message: res?.data?.message || "Manager Created Successfully",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "success",
          })
        );
        formik.resetForm();
        setpassengerimage("/assets/images/user/avatar.png");
        handleClose();
      }
    };

    const onFailure = (err) => {
      const messagesArray = err?.response?.data?.data || [];
      const messages = messagesArray.map((item) => item.msg).join(", ");
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: messages || err?.response?.data?.message || "Error while creating manager",
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
    };

    AccountmanagerApi.addManager({ ...data }).then(onSuccess, onFailure);
  };

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      passConfirmation: "",
      gender: "",
      country: "",
      mobile: "",
      countryCode: "",
      passengerProfileImage: "",
    },
    validationSchema: yup.object({
      firstName: yup.string().required("First Name is required").nullable(),
      email: yup.string().email("Invalid email format").required("Email is required").nullable(),
      countryCode: yup.string().required("Country Code is required").nullable(),
      mobile: yup
        .string()
        .matches(/^\d{1,15}$/, "Invalid mobile number")
        .required("Mobile is required")
        .nullable(),
      password: yup
        .string()
        .min(6, "Password must be at least 6 characters")
        .required("New password is required")
        .nullable(),
      passConfirmation: yup
        .string()
        .required("Confirm password is required")
        .oneOf([yup.ref("password"), null], "Passwords must match")
        .nullable(),
      gender: yup.string().required("Gender is required").nullable(),
      country: yup.string().required("Country is required").nullable(),
      passengerProfileImage: yup.mixed().required("Profile Image is required").nullable(),
    }),
    enableReinitialize: true,
    onSubmit: handleAddManager,
  });

  return (
    <Dialog
      open={openDialog}
      onClose={() => {
        formik.resetForm();
        handleClose();
      }}
      classes={{ paper: classes.dialogPaper }}
      maxWidth={false}
    >
      {/* Header */}
      <Box className={classes.dialogHeader}>
        <Typography className={classes.dialogHeaderTitle}>Add Account Manager</Typography>
        <IconButton className={classes.closeIconBtn} onClick={handleClose}>
          <CloseOutlined />
        </IconButton>
      </Box>

      {/* Body */}
      <Box className={classes.dialogBody}>
        {/* Left – Profile Photo */}
        <Box className={classes.leftColumn}>
          <Box className={classes.avatarWrapper}>
            <img
              src={passengerimage}
              alt="Profile"
              className={classes.avatarImg}
            />
            <Box
              component="span"
              className={classes.editIconOverlay}
              {...getRootProps()}
              onClick={open}
            >
              <input {...getInputProps()} />
              <EditOutlined />
            </Box>
          </Box>
          <Typography className={classes.photoLabel}>Profile Photo</Typography>
          <Typography className={classes.photoSubLabel}>JPG, PNG up to 5MB</Typography>
          {formik.errors.passengerProfileImage && formik.touched.passengerProfileImage && (
            <Typography color="error" variant="caption" style={{ marginTop: 4 }}>
              {formik.errors.passengerProfileImage}
            </Typography>
          )}
        </Box>

        {/* Right – Form */}
        <Box className={classes.rightColumn}>
          <Grid container spacing={2}>
            {/* First Name + Last Name */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>First Name</Typography>
              <TextField
                id="firstName"
                name="firstName"
                placeholder="e.g. John"
                value={formik.values.firstName}
                onChange={formik.handleChange}
                error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                helperText={formik.touched.firstName && formik.errors.firstName}
                fullWidth
                size="small"
                variant="outlined"
                className={classes.formInput}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Last Name</Typography>
              <TextField
                id="lastName"
                name="lastName"
                placeholder="e.g. Doe"
                value={formik.values.lastName}
                onChange={formik.handleChange}
                error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                helperText={formik.touched.lastName && formik.errors.lastName}
                fullWidth
                size="small"
                variant="outlined"
                className={classes.formInput}
              />
            </Grid>

            {/* Email */}
            <Grid item xs={12}>
              <Typography className={classes.formLabel}>Email Address</Typography>
              <TextField
                id="email"
                name="email"
                placeholder="john.doe@example.com"
                value={formik.values.email}
                onChange={formik.handleChange}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                fullWidth
                size="small"
                variant="outlined"
                className={classes.formInput}
              />
            </Grid>

            {/* Mobile Number */}
            <Grid item xs={12}>
              <Typography className={classes.formLabel}>Mobile Number</Typography>
              <Box className={classes.phoneRow}>
                <Box className={classes.countryCodeWrapper}>
                  <CountryCodeDropdown
                    formik={formik}
                    countryCodeData={countryCodeData}
                    name="countryCode"
                    id="countryCode"
                    label=""
                    error={formik.touched.countryCode && Boolean(formik.errors.countryCode)}
                    helperText={formik.touched.countryCode && formik.errors.countryCode}
                  />
                </Box>
                <TextField
                  name="mobile"
                  id="mobile"
                  placeholder="000 000 0000"
                  value={formik.values.mobile}
                  onChange={formik.handleChange}
                  error={formik.touched.mobile && Boolean(formik.errors.mobile)}
                  helperText={formik.touched.mobile && formik.errors.mobile}
                  fullWidth
                  size="small"
                  variant="outlined"
                  className={classes.formInput}
                />
              </Box>
            </Grid>

            {/* Password & Confirm Password */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Password</Typography>
              <TextField
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter password"
                value={formik.values.password}
                onChange={formik.handleChange}
                error={formik.touched.password && Boolean(formik.errors.password)}
                helperText={formik.touched.password && formik.errors.password}
                fullWidth
                size="small"
                variant="outlined"
                className={classes.formInput}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Confirm Password</Typography>
              <TextField
                id="passConfirmation"
                name="passConfirmation"
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm password"
                value={formik.values.passConfirmation}
                onChange={formik.handleChange}
                error={formik.touched.passConfirmation && Boolean(formik.errors.passConfirmation)}
                helperText={formik.touched.passConfirmation && formik.errors.passConfirmation}
                fullWidth
                size="small"
                variant="outlined"
                className={classes.formInput}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Gender & Country */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Gender</Typography>
              <TextField
                name="gender"
                id="gender"
                select
                value={formik.values.gender || ""}
                onChange={formik.handleChange}
                error={formik.touched.gender && Boolean(formik.errors.gender)}
                helperText={formik.touched.gender && formik.errors.gender}
                fullWidth
                size="small"
                variant="outlined"
                className={classes.formInput}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (v) =>
                    v ? v.charAt(0).toUpperCase() + v.slice(1) : <span style={{ color: "#9ca3af" }}>Select gender</span>,
                }}
              >
                {["male", "female"].map((option) => (
                  <MenuItem key={option} value={option}>
                    {option.charAt(0).toUpperCase() + option.slice(1)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Country</Typography>
              <TextField
                name="country"
                id="country"
                select
                value={formik.values.country || ""}
                onChange={formik.handleChange}
                error={formik.touched.country && Boolean(formik.errors.country)}
                helperText={formik.touched.country && formik.errors.country}
                fullWidth
                size="small"
                variant="outlined"
                className={classes.formInput}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (v) => v || <span style={{ color: "#9ca3af" }}>Select country</span>,
                }}
              >
                {countryCodeData.map((country) => (
                  <MenuItem key={country.code} value={country.name}>
                    {`${country.code} (${country.name})`}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Footer */}
      <Box className={classes.dialogFooter}>
        <Button
          className={classes.cancelButton}
          onClick={() => {
            formik.resetForm();
            handleClose();
          }}
        >
          Cancel
        </Button>
        <Button
          className={classes.submitButton}
          onClick={formik.handleSubmit}
          startIcon={<PersonAddAltOutlined />}
        >
          Add Manager
        </Button>
      </Box>
    </Dialog>
  );
};

export default ManagerCreate;
